// =============================================================================
// domain/calendar.ts — the month grid, its week column, and its records.
// -----------------------------------------------------------------------------
// Ported from the old client's `calendarRenderMonthGrid` and friends
// (index.html:28822-29001). Pure: no clock, no DOM. `today` is a parameter for
// the reason the Goals card taught — a function that reads the machine clock
// asserts a different answer every day it runs, and the test that pins it goes
// red on a Monday.
//
// ── 🚨 THE WEEK IS SUN–FRI PLUS A SUMMARY, NOT SEVEN DAYS ──
//
// The old client's grid is SIX day columns (Sunday through Friday) and a seventh
// cell holding the WEEK TOTAL. Saturday has no column at all — CME has no
// Saturday session, so the space goes to something worth reading instead.
//
// This client's first Calendar shipped Mon–Sun, seven days, no week column. That
// is a different calendar: it gives a column to a day that never trades, drops
// the weekly summary entirely, and starts the week on the wrong day.
//
// ── 🚨 …BUT THE WEEK TOTAL STILL COUNTS SATURDAY ──
//
// `weekStats` sums all SEVEN days from the Sunday, including the Saturday the
// grid does not draw. Beta's own comment says why: "so the week-summary cell
// never under-reports". A Saturday trade is vanishingly rare and would otherwise
// vanish from the week total while still counting in the month — two numbers on
// one screen disagreeing, which is the defect this rebuild keeps closing.
// =============================================================================

import { findContract, tradeValue, type ContractSpec, type ValuedTrade } from './trade';
import type { PnlUnit } from './types';

/** A day's totals. `hasJournal` drives the dot, not any number. */
export interface CalendarDay {
  pnl: number;
  trades: number;
  hasJournal: boolean;
  /**
   * The distinct contract roots traded that day — `MES`, `CL`, and so on.
   *
   * ⚠ CARRIED PER DAY so the period totals can tell whether they are summing
   * one instrument or several. Points and ticks do not add across instruments:
   * ES points and CL points are different quantities of different things, and a
   * month total that mixes them is a number with no meaning. Beta prints a gold
   * ⚠ beside the period P&L when that happens, and this is what feeds it (#67).
   */
  roots: readonly string[];
}

export type CalendarByDate = ReadonlyMap<string, CalendarDay>;

// ---------------------------------------------------------------------------
// Date arithmetic, on ISO strings
// ---------------------------------------------------------------------------

/** ⚠ UTC throughout. A local-time Date shifts the day across a DST boundary. */
function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function shiftDays(iso: string, n: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(iso: string): number {
  return utc(iso).getUTCDay();
}

/** `YYYY-MM` for the month containing `iso`. */
export function monthPrefix(iso: string): string {
  return iso.slice(0, 7);
}

/** The 1st of the month, `offset` months from the month containing `iso`. */
export function monthStart(iso: string, offset = 0): string {
  const d = utc(iso);
  return toIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1)));
}

/** The Sunday on or before `iso` — the start of beta's week. */
export function weekStart(iso: string): string {
  return shiftDays(iso, -weekday(iso));
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

export interface CalendarCell {
  date: string;
  dayOfMonth: number;
  /** False for the leading/trailing days of the neighbouring months. */
  inMonth: boolean;
  isToday: boolean;
  /** Null when the day has no trades — a real, ordinary fact. */
  day: CalendarDay | null;
  /** Best day OF THE DISPLAYED MONTH, and only when it is actually a winner. */
  isBest: boolean;
  isWorst: boolean;
}

export interface WeekSummary {
  /** The Sunday the row starts on. */
  start: string;
  pnl: number;
  trades: number;
  /** Days that traded, not days elapsed. */
  days: number;
  wins: number;
  losses: number;
  /** Null when no day resolved either way — never 0, which reads as "all losses". */
  winPct: number | null;
  isCurrentWeek: boolean;
}

export interface CalendarRow {
  cells: readonly CalendarCell[];
  week: WeekSummary;
}

/**
 * Totals for the seven days from `sunday`.
 *
 * ⚠ SEVEN, including the Saturday the grid does not draw. See the header.
 */
export function weekStats(byDate: CalendarByDate, sunday: string): Omit<WeekSummary, 'isCurrentWeek'> {
  let pnl = 0, trades = 0, days = 0, wins = 0, losses = 0;
  for (let i = 0; i < 7; i++) {
    const d = byDate.get(shiftDays(sunday, i));
    if (!d) continue;
    pnl += d.pnl;
    trades += d.trades;
    days++;
    if (d.pnl > 0) wins++;
    else if (d.pnl < 0) losses++;
  }
  const decided = wins + losses;
  return {
    start: sunday, pnl, trades, days, wins, losses,
    winPct: decided ? Math.round((wins / decided) * 100) : null,
  };
}

export interface MonthRecords {
  best: { date: string; pnl: number } | null;
  worst: { date: string; pnl: number } | null;
}

/**
 * The month's extremes.
 *
 * ⚠ SEEDED FROM THE FIRST DAY REGARDLESS OF SIGN, exactly as beta does — so on
 * an all-red month `best` is the least-bad day. That is why the CELL checks the
 * sign again before showing a star: "best day" on a −$40 loss would be a joke at
 * the trader's expense.
 */
export function monthRecords(byDate: CalendarByDate, monthIso: string): MonthRecords {
  const prefix = monthPrefix(monthIso);
  let best: MonthRecords['best'] = null;
  let worst: MonthRecords['worst'] = null;
  for (const [date, d] of byDate) {
    if (!date.startsWith(prefix)) continue;
    if (!best || d.pnl > best.pnl) best = { date, pnl: d.pnl };
    if (!worst || d.pnl < worst.pnl) worst = { date, pnl: d.pnl };
  }
  return { best, worst };
}

/**
 * Six rows of six days (Sun–Fri), each with its week total.
 *
 * ⚠ ALWAYS SIX ROWS, even when the month fits in five. Beta draws a fixed grid
 * so the card does not change height as you page through months, which is the
 * kind of jump that makes a calendar feel broken.
 */
export function monthGrid(
  byDate: CalendarByDate,
  monthIso: string,
  today: string,
): readonly CalendarRow[] {
  const first = monthStart(monthIso);
  const prefix = monthPrefix(first);
  const firstSunday = weekStart(first);
  const records = monthRecords(byDate, first);
  const thisWeek = weekStart(today);

  const rows: CalendarRow[] = [];
  for (let r = 0; r < 6; r++) {
    const rowStart = shiftDays(firstSunday, r * 7);
    const cells: CalendarCell[] = [];
    // ⚠ SIX, not seven. The Saturday column is the week summary.
    for (let c = 0; c < 6; c++) {
      const date = shiftDays(rowStart, c);
      const day = byDate.get(date) ?? null;
      cells.push({
        date,
        dayOfMonth: utc(date).getUTCDate(),
        inMonth: date.startsWith(prefix),
        isToday: date === today,
        day,
        // ⚠ The sign is re-checked here. See `monthRecords`.
        isBest: !!day && !!records.best && date === records.best.date && day.pnl > 0,
        isWorst: !!day && !!records.worst && date === records.worst.date && day.pnl < 0,
      });
    }
    rows.push({
      cells,
      week: { ...weekStats(byDate, rowStart), isCurrentWeek: rowStart === thisWeek },
    });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// The four tiles
// ---------------------------------------------------------------------------

export interface CalendarKpis {
  pnl: number;
  trades: number;
  /** Days that traded. */
  tradingDays: number;
  /** Days that finished green. A breakeven day is neither. */
  winDays: number;
  /**
   * How many distinct instruments the period's trades span.
   *
   * ⚠ More than one, in points or ticks, means `pnl` above is a sum of things
   * that do not add — the screen warns rather than hiding it. The UNIT test is
   * deliberately not made here: this counts instruments, the caller decides
   * whether that matters in the unit on screen.
   */
  instruments: number;
}

/**
 * Totals for any ISO prefix — `2026-07` for a month, `2026` for a year.
 *
 * ⚠ One function for both, because beta computes them identically and only
 * differs in the prefix. Two functions would be two places to get "win day"
 * wrong.
 */
export function calendarKpis(byDate: CalendarByDate, prefix: string): CalendarKpis {
  let pnl = 0, trades = 0, tradingDays = 0, winDays = 0;
  const roots = new Set<string>();
  for (const [date, d] of byDate) {
    if (!date.startsWith(prefix)) continue;
    pnl += d.pnl;
    trades += d.trades;
    tradingDays++;
    if (d.pnl > 0) winDays++;
    for (const r of d.roots) roots.add(r);
  }
  return { pnl, trades, tradingDays, winDays, instruments: roots.size };
}

// ---------------------------------------------------------------------------
// Streaks
// ---------------------------------------------------------------------------

export interface Streaks {
  longestWin: number;
  longestLoss: number;
  /** 1 winning, -1 losing, 0 none in progress. */
  currentDir: -1 | 0 | 1;
  currentLen: number;
}

/**
 * Longest and current runs, across ALL history in the map.
 *
 * ⚠ A FLAT DAY RESETS THE RUN AND COUNTS AS NEITHER. Beta's rule, and it is the
 * right one: a scratch day did not break the trader's form and it did not extend
 * it either. Treating it as a loss would make an honest breakeven day end a
 * winning streak, which is a claim about discipline the data does not support.
 *
 * ⚠ "Current" is the MOST RECENT run, not a run that must reach today. A trader
 * looking on Sunday still wants to know where Friday left them.
 */
export function streaks(byDate: CalendarByDate): Streaks {
  const dates = [...byDate.keys()].sort();
  let longestWin = 0, longestLoss = 0;
  let dir: -1 | 0 | 1 = 0, len = 0;
  let curDir: -1 | 0 | 1 = 0, curLen = 0;

  for (const date of dates) {
    const pnl = byDate.get(date)?.pnl ?? 0;
    const d: -1 | 0 | 1 = pnl > 0 ? 1 : pnl < 0 ? -1 : 0;
    if (d === 0) { dir = 0; len = 0; continue; }
    if (d === dir) len++;
    else { dir = d; len = 1; }
    if (d === 1 && len > longestWin) longestWin = len;
    if (d === -1 && len > longestLoss) longestLoss = len;
    curDir = dir; curLen = len;
  }
  return { longestWin, longestLoss, currentDir: curDir, currentLen: curLen };
}

/** The best month of a calendar year, by total. `year` is `YYYY`. */
export function bestMonthOfYear(
  byDate: CalendarByDate,
  year: string,
): { month: string; pnl: number } | null {
  const totals = new Map<string, number>();
  for (const [date, d] of byDate) {
    if (!date.startsWith(`${year}-`)) continue;
    const key = date.slice(0, 7);
    totals.set(key, (totals.get(key) ?? 0) + d.pnl);
  }
  let best: { month: string; pnl: number } | null = null;
  for (const [month, pnl] of totals) if (!best || pnl > best.pnl) best = { month, pnl };
  return best;
}

// ---------------------------------------------------------------------------
// Day-of-week breakdown
// ---------------------------------------------------------------------------

export interface DowBucket {
  name: string;
  pnl: number;
  trades: number;
  /** Days that traded on this weekday. */
  days: number;
  wins: number;
  losses: number;
  /** Mean per TRADING DAY, not per trade. 0 when the weekday never traded. */
  avg: number;
  winPct: number | null;
  /** Best/worst weekday by average, and only once there is enough of it. */
  strongest: boolean;
  weakest: boolean;
}

/** Beta requires five trading days on a weekday before it will call it either way. */
export const DOW_BADGE_MIN_DAYS = 5;

/**
 * Monday to Friday, across all history.
 *
 * ⚠ WEEKENDS ARE DROPPED, not bucketed into a sixth and seventh row. A Saturday
 * trade is a data artefact rather than a trading habit, and a row reading
 * "Sat · 1 day · -$400 · WEAKEST" would be a conclusion drawn from one fill.
 *
 * ⚠ The average is per TRADING DAY. Per trade would rank a weekday the trader
 * barely touches above one they work, which is the opposite of what the card is
 * for.
 */
export function dowBreakdown(byDate: CalendarByDate): readonly DowBucket[] {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  const raw = names.map((name) => ({ name, pnl: 0, trades: 0, days: 0, wins: 0, losses: 0 }));

  for (const [date, d] of byDate) {
    const dow = weekday(date);
    if (dow < 1 || dow > 5) continue;
    const b = raw[dow - 1];
    if (!b) continue;
    b.pnl += d.pnl;
    b.trades += d.trades;
    b.days++;
    if (d.pnl > 0) b.wins++;
    else if (d.pnl < 0) b.losses++;
  }

  const avgs = raw.map((b) => (b.days ? b.pnl / b.days : 0));
  const max = Math.max(...avgs);
  const min = Math.min(...avgs);
  const strongestAt = avgs.indexOf(max);
  const weakestAt = avgs.indexOf(min);

  return raw.map((b, i) => {
    const decided = b.wins + b.losses;
    const avg = avgs[i] ?? 0;
    return {
      ...b,
      avg,
      winPct: b.days ? Math.round((decided ? b.wins / decided : 0) * 100) : null,
      // ⚠ A badge needs BOTH enough days AND the right sign. "Strongest" on a
      // weekday that still loses money is a compliment nobody earned.
      strongest: b.days >= DOW_BADGE_MIN_DAYS && i === strongestAt && avg > 0,
      weakest: b.days >= DOW_BADGE_MIN_DAYS && i === weakestAt && avg < 0,
    };
  });
}

/** Bar width as a percentage of half the track — the bars grow from the centre. */
export function dowBarPct(buckets: readonly DowBucket[], avg: number): number {
  const maxAbs = Math.max(...buckets.map((b) => Math.abs(b.avg)), 1);
  return (Math.abs(avg) / maxAbs) * 50;
}

// ---------------------------------------------------------------------------
// Year view — twelve mini-calendars
// ---------------------------------------------------------------------------

/** Beta's `$`-calibrated emphasis: a day that actually mattered. */
export const HEAVY_DAY_USD = 250;

export interface MiniDay {
  date: string;
  inMonth: boolean;
  dayOfMonth: number;
  tone: '' | 'win' | 'loss';
  heavy: boolean;
  /** Null when there is nothing to open. */
  day: CalendarDay | null;
}

export interface MiniMonth {
  /** 0 = January. */
  index: number;
  /** `YYYY-MM`. */
  month: string;
  pnl: number;
  trades: number;
  days: number;
  winPct: number | null;
  cells: readonly MiniDay[];
}

/**
 * Twelve months, each on the SAME six-day grid as the month view.
 *
 * ⚠ Six columns and six rows, exactly as the big grid — beta's own on-screen note
 * says so. A year view that laid out seven days would teach a different shape
 * from the tab beside it.
 */
export function yearMonths(byDate: CalendarByDate, year: string): readonly MiniMonth[] {
  const out: MiniMonth[] = [];
  for (let m = 0; m < 12; m++) {
    const monthIso = `${year}-${String(m + 1).padStart(2, '0')}`;
    const first = `${monthIso}-01`;
    let pnl = 0, trades = 0, days = 0, wins = 0, losses = 0;
    for (const [date, d] of byDate) {
      if (!date.startsWith(monthIso)) continue;
      pnl += d.pnl; trades += d.trades; days++;
      if (d.pnl > 0) wins++; else if (d.pnl < 0) losses++;
    }

    const gridStart = weekStart(first);
    const cells: MiniDay[] = [];
    for (let r = 0; r < 6; r++) {
      for (let c = 0; c < 6; c++) {
        const date = shiftDays(gridStart, r * 7 + c);
        const inMonth = date.startsWith(monthIso);
        const day = inMonth ? byDate.get(date) ?? null : null;
        cells.push({
          date,
          inMonth,
          dayOfMonth: Number(date.slice(8)),
          tone: day ? (day.pnl > 0 ? 'win' : day.pnl < 0 ? 'loss' : '') : '',
          heavy: !!day && Math.abs(day.pnl) > HEAVY_DAY_USD,
          day,
        });
      }
    }

    const decided = wins + losses;
    out.push({
      index: m, month: monthIso, pnl, trades, days,
      winPct: decided ? Math.round((wins / decided) * 100) : null,
      cells,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Year heatmap
// ---------------------------------------------------------------------------

export interface HeatCell {
  date: string;
  inYear: boolean;
  /** 0 = no trading, 1–4 = intensity relative to the year's biggest day. */
  level: 0 | 1 | 2 | 3 | 4;
  tone: '' | 'win' | 'loss';
  day: CalendarDay | null;
}

export interface Heatmap {
  cells: readonly HeatCell[];
  /** Columns in the grid — one per week. */
  weeks: number;
  /** Where each month's name sits, by column. */
  monthLabels: readonly { week: number; month: number }[];
}

/**
 * A GitHub-style year grid: seven rows (Sun–Sat), one column per week.
 *
 * ⚠ IT STARTS ON THE SUNDAY BEFORE JANUARY 1st, so the first column is usually
 * part of December. Those cells are `inYear: false` and stay blank — without
 * them the first week would be short and every row would be off by a day.
 *
 * ⚠ INTENSITY IS RELATIVE TO THE YEAR'S OWN BIGGEST DAY, not to a fixed dollar
 * scale. A quiet year and a violent one both fill the range, which is the point:
 * the card answers "when was I active and how did it go", not "how much".
 */
export function yearHeatmap(byDate: CalendarByDate, year: string): Heatmap {
  let maxAbs = 1;
  for (const [date, d] of byDate) {
    if (!date.startsWith(`${year}-`)) continue;
    if (Math.abs(d.pnl) > maxAbs) maxAbs = Math.abs(d.pnl);
  }

  const yearEnd = `${year}-12-31`;
  const cells: HeatCell[] = [];
  const monthLabels: { week: number; month: number }[] = [];
  let week = 0;
  let lastMonth = -1;

  for (let date = weekStart(`${year}-01-01`); date <= yearEnd; date = shiftDays(date, 1)) {
    const inYear = date.startsWith(`${year}-`);
    const day = inYear ? byDate.get(date) ?? null : null;
    // ⚠ `|| 1` — a day whose P&L rounds to a zero level still gets level 1. A
    // day that was traded must never look identical to one that was not.
    const level = day
      ? (Math.min(4, Math.ceil(Math.abs(day.pnl) / (maxAbs / 4))) || 1) as 1 | 2 | 3 | 4
      : 0;
    cells.push({
      date, inYear, level,
      tone: day ? (day.pnl > 0 ? 'win' : 'loss') : '',
      day,
    });

    const dow = weekday(date);
    const month = Number(date.slice(5, 7)) - 1;
    if (dow === 0 && inYear && month !== lastMonth) {
      monthLabels.push({ week, month });
      lastMonth = month;
    }
    if (dow === 6) week++;
  }

  return { cells, weeks: week + 1, monthLabels };
}

// ---------------------------------------------------------------------------
// The day totals
// ---------------------------------------------------------------------------

/** What `calendarDays` needs from a trade. */
export interface CalendarTrade extends ValuedTrade {
  /** Already resolved by the caller — session mode moves a late trade. */
  effectiveDate: string | null;
}

/**
 * One entry per day: total in `unit`, trade count, journal flag.
 *
 * 🚨 THIS WAS A LOOP INSIDE THE CALENDAR COMPONENT, SUMMING `netPnl`. That is
 * the dollar figure whatever the display unit says, so the whole tab was
 * structurally incapable of following the switch — and swapping its formatters
 * alone would have put a "pts" suffix on dollar sums, which is worse than
 * leaving it alone. Aggregation belongs here, where it can be tested.
 *
 * ⚠ A TRADE WITH NO VALUE IN THE UNIT STILL COUNTS AS A TRADE. In R a
 * stop-less trade has no realised R; it contributes nothing to the total and
 * is still one of the day’s trades. That mirrors the rule already in place for
 * a null P&L — an open position is not a scratch, and dropping it would
 * understate the day’s activity while flattering nothing.
 */
export function calendarDays(
  rows: readonly CalendarTrade[],
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
  journalled: ReadonlySet<string> | null,
): Map<string, CalendarDay> {
  const map = new Map<string, CalendarDay>();
  for (const t of rows) {
    const d = t.effectiveDate;
    if (!d) continue;
    const cur = map.get(d) ?? { pnl: 0, trades: 0, hasJournal: false, roots: [] };
    const v = tradeValue(t, unit, catalogue);
    // ⚠ THE CATALOGUE SYMBOL, falling back to the raw contract. `MESU26` and
    // `MESZ26` are one instrument across a roll; treating them as two would put
    // a mixed-instrument warning on every month that spans a contract change.
    // Same resolution `computeKpis` uses for its own `mixed` flag.
    const spec = findContract(t.contract, catalogue);
    const root = spec ? spec.symbol : (t.contract || null);
    map.set(d, {
      pnl: cur.pnl + (v ?? 0),
      trades: cur.trades + 1,
      hasJournal: journalled?.has(d) ?? false,
      roots: root && !cur.roots.includes(root) ? [...cur.roots, root] : cur.roots,
    });
  }
  return map;
}
